// Print/Export Utility Functions
// Sprint 1: Foundation - Utils Module

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// REMOVED: import { PDFDocument, PDFName, PDFString } from 'pdf-lib';
// We'll use dynamic import instead to avoid bundle size issues
import type { 
  PrintConfig, 
  PrintArea, 
  ExportTable,
  ExportRow,
  ExportCell,
  PrintMetadata,
} from '@/app/types/print';
import type { TableConfig, TableRow, TableColumn, TableCell, CellStyle } from '@/app/types/table';
import { PAGE_SIZE_DIMENSIONS } from '@/app/types/print';
import { generateExportFileName } from './printConstants';
import { saveFileWithDialog } from './projectIO';

// ===== PDF Metadata Helpers =====

/**
 * Set PDF metadata using pdf-lib (Hybrid Approach)
 * This function takes a jsPDF-generated PDF and injects comprehensive metadata
 * including custom fields into the PDF Info Dictionary using pdf-lib.
 * 
 * Why pdf-lib?
 * - jsPDF has a bug where Keywords are not written to the PDF
 * - jsPDF cannot write custom fields to the Info Dictionary
 * - pdf-lib gives us low-level access to the PDF structure
 * 
 * @param pdfBuffer - ArrayBuffer from jsPDF output
 * @param metadata - Print metadata including custom fields
 * @param includeCustomFieldsInKeywords - Whether to include custom fields in keywords
 * @returns Modified PDF as Uint8Array
 */
async function setPDFMetadataWithPdfLib(
  pdfBuffer: ArrayBuffer,
  metadata: PrintMetadata,
  includeCustomFieldsInKeywords: boolean
): Promise<Uint8Array> {
  // DYNAMIC IMPORT: Load pdf-lib only when needed
  // This prevents bundle size issues and lazy-loads the library
  const { PDFDocument, PDFName, PDFString } = await import('pdf-lib');
  
  // Load the PDF generated by jsPDF
  const pdfDoc = await PDFDocument.load(pdfBuffer);
  
  // Build keywords array - Add ALL metadata fields as structured keywords
  // This ensures they are searchable and editable even in simple PDF editors
  const keywords: string[] = [];
  
  // Add project metadata as keywords
  if (metadata.projectName) {
    keywords.push(`Projekt: ${metadata.projectName}`);
  }
  
  if (metadata.projectDescription) {
    keywords.push(`Beschreibung: ${metadata.projectDescription}`);
  }
  
  if (metadata.author) {
    keywords.push(`Autor: ${metadata.author}`);
  }
  
  if (metadata.companyName) {
    keywords.push(`Firma: ${metadata.companyName}`);
  }
  
  // Add version and dates to keywords for searchability
  keywords.push(`Version: ${metadata.version}`);
  keywords.push(`Erstellt: ${metadata.dateCreated.toLocaleDateString('de-DE')}`);
  keywords.push(`Geändert: ${metadata.dateModified.toLocaleDateString('de-DE')}`);
  keywords.push(`Gedruckt: ${metadata.datePrinted.toLocaleDateString('de-DE')}`);
  
  // Add module information
  keywords.push(`Modul: SYSTEM.TABLE`);
  
  // Add custom fields to keywords - ALWAYS, regardless of display setting
  // The display setting only controls where they appear in the document,
  // but keywords should always be comprehensive for searchability
  if (metadata.customFields && Object.keys(metadata.customFields).length > 0) {
    Object.entries(metadata.customFields).forEach(([key, value]) => {
      if (value) {
        keywords.push(`${key}: ${value}`);
      }
    });
  }
  
  // Build comprehensive subject line
  let subject = metadata.projectDescription || '';
  if (!subject && metadata.version) {
    subject = `Version ${metadata.version}`;
  } else if (subject && metadata.version) {
    subject = `${subject} (Version ${metadata.version})`;
  }
  
  // Build comprehensive author line (author + company)
  let author = metadata.author || '';
  if (metadata.companyName) {
    author = author 
      ? `${author} (${metadata.companyName})`
      : metadata.companyName;
  }
  if (!author) {
    author = 'ISOBLOCK User';
  }
  
  const keywordsString = keywords.join(', ');
  
  // Set standard PDF metadata fields
  pdfDoc.setTitle(metadata.projectName || 'Unbenanntes Projekt');
  pdfDoc.setSubject(subject || `Tabellen-Export vom ${metadata.datePrinted.toLocaleDateString('de-DE')}`);
  pdfDoc.setAuthor(author);
  pdfDoc.setKeywords([keywordsString]); // pdf-lib uses array format
  pdfDoc.setProducer('ISOBLOCK SYSTEM.TABLE');
  pdfDoc.setCreator('ISOBLOCK SYSTEM.TABLE');
  pdfDoc.setCreationDate(metadata.dateCreated);
  pdfDoc.setModificationDate(metadata.datePrinted);
  
  // LOW-LEVEL: Access the PDF Info Dictionary to add custom fields
  // This is where the magic happens - we write custom metadata that survives PDF readers
  const context = pdfDoc.context;
  let infoDict = context.lookup(context.trailerInfo.Info);
  
  if (!infoDict) {
    // Create new Info Dictionary if it doesn't exist (unlikely)
    infoDict = context.obj({});
    context.trailerInfo.Info = context.register(infoDict);
  }
  
  // Add custom fields to the Info Dictionary
  // These will be visible in Adobe Acrobat Pro under File -> Properties -> Custom
  if (metadata.customFields && Object.keys(metadata.customFields).length > 0) {
    Object.entries(metadata.customFields).forEach(([key, value]) => {
      if (value) {
        // PDF keys must start with / and contain no spaces
        // We prefix with "Custom_" to avoid conflicts with standard fields
        const pdfKey = `Custom_${key.replace(/\s+/g, '_')}`;
        infoDict.set(PDFName.of(pdfKey), PDFString.of(String(value)));
      }
    });
  }
  
  // Also add version as custom field for easy parsing
  infoDict.set(PDFName.of('ISOBLOCK_Version'), PDFString.of(metadata.version));
  infoDict.set(PDFName.of('ISOBLOCK_Module'), PDFString.of('SYSTEM.TABLE'));
  
  // Add company name as custom field if available
  if (metadata.companyName) {
    infoDict.set(PDFName.of('Company'), PDFString.of(metadata.companyName));
  }
  
  // Debug: Log what we're setting
  console.log('PDF Metadata set via pdf-lib:', {
    title: metadata.projectName || 'Unbenanntes Projekt',
    subject: subject || `Tabellen-Export vom ${metadata.datePrinted.toLocaleDateString('de-DE')}`,
    author: author,
    keywords: keywordsString,
    keywordsLength: keywordsString.length,
    producer: 'ISOBLOCK SYSTEM.TABLE',
    creator: 'ISOBLOCK SYSTEM.TABLE',
    creationDate: metadata.dateCreated.toISOString(),
    modificationDate: metadata.datePrinted.toISOString(),
    customFieldsCount: metadata.customFields ? Object.keys(metadata.customFields).length : 0,
  });
  
  // Save and return the modified PDF
  return await pdfDoc.save();
}

// ===== Print Area Calculation =====

/**
 * Calculate which rows and columns to include based on PrintArea configuration
 */
export function calculatePrintArea(
  area: PrintArea,
  tableConfig: TableConfig
): { rows: TableRow[]; columns: TableColumn[] } {
  const { columns, rows } = tableConfig;
  
  let selectedRows: TableRow[] = [];
  let selectedColumns: TableColumn[] = [];
  
  switch (area.type) {
    case 'current-table':
      // All visible rows and columns
      selectedRows = rows.filter(r => !r.isEmpty);
      selectedColumns = columns.filter(c => c.isVisible !== false);
      break;
      
    case 'selected-range':
      // Specific range of cells
      if (
        area.startRow !== undefined && 
        area.endRow !== undefined && 
        area.startColumn !== undefined && 
        area.endColumn !== undefined
      ) {
        selectedRows = rows.slice(area.startRow, area.endRow + 1);
        selectedColumns = columns.slice(area.startColumn, area.endColumn + 1);
      }
      break;
      
    case 'selected-columns':
      // All rows, but only selected columns
      selectedRows = rows.filter(r => !r.isEmpty);
      if (area.columnIds && area.columnIds.length > 0) {
        selectedColumns = columns.filter(c => area.columnIds!.includes(c.id));
      }
      break;
      
    case 'selected-rows':
      // All columns, but only selected rows
      selectedColumns = columns.filter(c => c.isVisible !== false);
      if (area.rowIds && area.rowIds.length > 0) {
        selectedRows = rows.filter(r => area.rowIds!.includes(r.id));
      }
      break;
      
    case 'custom-selection':
      // Similar to selected-range but user-defined
      if (
        area.startRow !== undefined && 
        area.endRow !== undefined && 
        area.startColumn !== undefined && 
        area.endColumn !== undefined
      ) {
        selectedRows = rows.slice(area.startRow, area.endRow + 1);
        selectedColumns = columns.slice(area.startColumn, area.endColumn + 1);
      }
      break;
  }
  
  return { rows: selectedRows, columns: selectedColumns };
}

/**
 * Build an ExportTable structure from TableConfig and PrintArea
 */
export function buildExportTable(
  tableConfig: TableConfig,
  area: PrintArea,
  metadata?: PrintMetadata
): ExportTable {
  const { rows, columns } = calculatePrintArea(area, tableConfig);
  
  const exportRows: ExportRow[] = [];
  
  // Add header rows if requested (HeaderRows approach)
  if (area.includeHeaderRows && tableConfig.headerRows && tableConfig.headerRows.length > 0) {
    for (const headerRow of tableConfig.headerRows) {
      const headerCells: ExportCell[] = [];
      
      for (const headerCell of headerRow.cells) {
        headerCells.push({
          value: headerCell.label,
          rowIndex: exportRows.length,
          colIndex: headerCell.columnStartIndex,
          rowSpan: headerCell.rowspan || 1,
          colSpan: headerCell.colspan,
          style: headerCell.style,
          isHeader: true,
        });
      }
      
      exportRows.push({
        cells: headerCells,
        isHeader: true,
      });
    }
  }
  
  // Add column headers if requested
  if (area.includeHeaders) {
    const headerCells: ExportCell[] = columns.map((col, idx) => ({
      value: col.label,
      rowIndex: exportRows.length,
      colIndex: idx,
      rowSpan: 1,
      colSpan: 1,
      isHeader: true,
    }));
    
    exportRows.push({
      cells: headerCells,
      isHeader: true,
    });
  }
  
  // Add data rows
  for (const row of rows) {
    const dataCells: ExportCell[] = columns.map((col, idx) => {
      const cell = row.data[col.id];
      
      return {
        value: cell?.value ?? '',
        rowIndex: exportRows.length,
        colIndex: idx,
        rowSpan: cell?.merge?.rowSpan ?? 1,
        colSpan: cell?.merge?.colSpan ?? 1,
        style: cell?.style,
        isHeader: false,
      };
    });
    
    exportRows.push({
      cells: dataCells,
      isHeader: false,
    });
  }
  
  return {
    rows: exportRows,
    columns,
    metadata,
  };
}

// ===== PDF Export =====

/**
 * Export table data as PDF using jsPDF and autoTable
 * Then inject metadata using pdf-lib (Hybrid Approach)
 */
export async function exportToPDF(config: PrintConfig, tableConfig: TableConfig): Promise<Blob> {
  const { format, metadata, area } = config;
  
  if (!format.pdf) {
    throw new Error('PDF options not configured');
  }
  
  const pdfOptions = format.pdf;
  
  // Determine page size
  let pageWidth = 210; // A4 default
  let pageHeight = 297;
  
  if (pdfOptions.pageSize !== 'Auto') {
    const dimensions = PAGE_SIZE_DIMENSIONS[pdfOptions.pageSize];
    pageWidth = dimensions.width;
    pageHeight = dimensions.height;
  }
  
  // ==========================================
  // STEP 1: Generate PDF Layout with jsPDF
  // ==========================================
  const doc = new jsPDF({
    orientation: pdfOptions.orientation,
    unit: 'mm',
    format: pdfOptions.pageSize === 'Auto' ? 'a4' : pdfOptions.pageSize.toLowerCase(),
  });
  
  let currentY = pdfOptions.margins.top;
  
  // Add metadata header if requested
  if (pdfOptions.includeMetadataHeader) {
    doc.setFontSize(14);
    doc.text(metadata.projectName, pdfOptions.margins.left, currentY);
    currentY += 7;
    
    doc.setFontSize(10);
    if (metadata.projectDescription) {
      const lines = doc.splitTextToSize(
        metadata.projectDescription,
        pageWidth - pdfOptions.margins.left - pdfOptions.margins.right
      );
      doc.text(lines, pdfOptions.margins.left, currentY);
      currentY += lines.length * 5;
    }
    
    doc.setFontSize(8);
    
    // Standard metadata
    if (metadata.author) {
      doc.text(`Autor: ${metadata.author}`, pdfOptions.margins.left, currentY);
      currentY += 4;
    }
    
    if (metadata.companyName) {
      doc.text(`Firma: ${metadata.companyName}`, pdfOptions.margins.left, currentY);
      currentY += 4;
    }
    
    doc.text(`Version: ${metadata.version}`, pdfOptions.margins.left, currentY);
    currentY += 4;
    
    doc.text(`Erstellt: ${metadata.dateCreated.toLocaleDateString('de-DE')}`, pdfOptions.margins.left, currentY);
    currentY += 4;
    
    doc.text(`Geändert: ${metadata.dateModified.toLocaleDateString('de-DE')}`, pdfOptions.margins.left, currentY);
    currentY += 4;
    
    doc.text(`Gedruckt: ${metadata.datePrinted.toLocaleString('de-DE')}`, pdfOptions.margins.left, currentY);
    currentY += 4;
    
    // Custom fields - ONLY if customFieldsDisplay is 'above-table'
    if (pdfOptions.customFieldsDisplay === 'above-table' && 
        metadata.customFields && 
        Object.keys(metadata.customFields).length > 0) {
      currentY += 2; // Extra spacing before custom fields
      
      doc.setFont(undefined, 'bold');
      doc.text('Benutzerdefinierte Felder:', pdfOptions.margins.left, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 4;
      
      Object.entries(metadata.customFields).forEach(([key, value]) => {
        if (value) {
          doc.text(`${key}: ${value}`, pdfOptions.margins.left, currentY);
          currentY += 4;
        }
      });
    }
    
    currentY += 5; // Extra spacing before table
  }
  
  // Build export table
  const exportTable = buildExportTable(tableConfig, area, metadata);
  
  // Convert to autoTable format
  const tableData: any[][] = [];
  let headerRows: any[][] = [];
  
  for (const row of exportTable.rows) {
    const rowData = row.cells.map(cell => cell.value);
    
    if (row.isHeader) {
      headerRows.push(rowData);
    } else {
      tableData.push(rowData);
    }
  }
  
  // Generate table with autoTable
  autoTable(doc, {
    head: headerRows.length > 0 ? headerRows : undefined,
    body: tableData,
    startY: currentY,
    margin: {
      top: pdfOptions.margins.top,
      right: pdfOptions.margins.right,
      bottom: pdfOptions.margins.bottom,
      left: pdfOptions.margins.left,
    },
    styles: {
      fontSize: pdfOptions.fontSize,
      font: pdfOptions.fontFamily,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [66, 66, 66],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    theme: pdfOptions.includeGridLines ? 'grid' : 'plain',
    showHead: 'firstPage',
  });
  
  // Add custom fields below table if customFieldsDisplay is 'below-table'
  if (pdfOptions.customFieldsDisplay === 'below-table' && 
      metadata.customFields && 
      Object.keys(metadata.customFields).length > 0) {
    // Get the Y position after the table
    const finalY = (doc as any).lastAutoTable.finalY || currentY;
    let belowTableY = finalY + 10; // 10mm spacing after table
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text('Benutzerdefinierte Felder:', pdfOptions.margins.left, belowTableY);
    doc.setFont(undefined, 'normal');
    belowTableY += 4;
    
    Object.entries(metadata.customFields).forEach(([key, value]) => {
      if (value) {
        doc.text(`${key}: ${value}`, pdfOptions.margins.left, belowTableY);
        belowTableY += 4;
      }
    });
  }
  
  // Add page numbers if requested
  if (pdfOptions.includePageNumbers) {
    const pageCount = doc.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      
      const pageText = `Page ${i} of ${pageCount}`;
      const textWidth = doc.getTextWidth(pageText);
      
      let xPos = pdfOptions.margins.left;
      if (pdfOptions.pageNumberPosition === 'bottom-center') {
        xPos = (pageWidth - textWidth) / 2;
      } else if (pdfOptions.pageNumberPosition === 'bottom-right') {
        xPos = pageWidth - pdfOptions.margins.right - textWidth;
      }
      
      doc.text(pageText, xPos, pageHeight - pdfOptions.margins.bottom + 5);
    }
  }
  
  // ==========================================
  // STEP 2: Inject Metadata with pdf-lib
  // ==========================================
  // Get PDF as ArrayBuffer from jsPDF
  const pdfBuffer = doc.output('arraybuffer');
  
  // Inject comprehensive metadata using pdf-lib
  const pdfBytesWithMetadata = await setPDFMetadataWithPdfLib(
    pdfBuffer,
    metadata,
    pdfOptions.customFieldsDisplay === 'above-table'
  );
  
  // Return as Blob
  return new Blob([pdfBytesWithMetadata], { type: 'application/pdf' });
}

// ===== Excel/CSV Export =====

/**
 * Export table data as CSV
 */
export function exportToCSV(config: PrintConfig, tableConfig: TableConfig): Blob {
  const { format, area, metadata } = config;
  
  if (!format.excel) {
    throw new Error('Excel options not configured');
  }
  
  const excelOptions = format.excel;
  const separator = excelOptions.separator;
  
  // Build export table
  const exportTable = buildExportTable(tableConfig, area);
  
  // Convert to CSV string
  const csvRows: string[] = [];
  
  // Add metadata header if enabled
  if (excelOptions.includeProjectMetadata) {
    csvRows.push('# PROJEKTINFORMATIONEN');
    csvRows.push(`# Projektname: ${metadata.projectName}`);
    
    if (metadata.projectDescription) {
      csvRows.push(`# Beschreibung: ${metadata.projectDescription}`);
    }
    
    if (metadata.author) {
      csvRows.push(`# Autor: ${metadata.author}`);
    }
    
    if (metadata.companyName) {
      csvRows.push(`# Firma: ${metadata.companyName}`);
    }
    
    csvRows.push(`# Version: ${metadata.version}`);
    csvRows.push(`# Erstellt: ${metadata.dateCreated.toLocaleDateString('de-DE')}`);
    csvRows.push(`# Geändert: ${metadata.dateModified.toLocaleDateString('de-DE')}`);
    csvRows.push(`# Exportiert: ${metadata.datePrinted.toLocaleString('de-DE')}`);
    
    // Add custom fields if any
    if (metadata.customFields && Object.keys(metadata.customFields).length > 0) {
      csvRows.push('#');
      csvRows.push('# BENUTZERDEFINIERTE FELDER');
      
      Object.entries(metadata.customFields).forEach(([key, value]) => {
        if (value) {
          csvRows.push(`# ${key}: ${value}`);
        }
      });
    }
    
    csvRows.push('#');
    csvRows.push('# TABELLENDATEN');
    csvRows.push('#');
  }
  
  // Add table data
  for (const row of exportTable.rows) {
    const csvRow = row.cells.map(cell => {
      let value = String(cell.value);
      
      // Escape quotes and wrap in quotes if contains separator or quotes
      if (value.includes(separator) || value.includes('"') || value.includes('\n')) {
        value = '"' + value.replace(/"/g, '""') + '"';
      }
      
      return value;
    }).join(separator);
    
    csvRows.push(csvRow);
  }
  
  const csvContent = csvRows.join('\n');
  
  // Return as Blob with UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  return new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
}

// ===== Download Helper =====

/**
 * Trigger browser download for a Blob using "Save As" dialog if available
 */
export async function downloadBlob(blob: Blob, fileName: string): Promise<void> {
  const extension = fileName.split('.').pop() || '';
  let mimeType = 'application/octet-stream';
  let description = 'File';
  
  if (extension.toLowerCase() === 'pdf') {
    mimeType = 'application/pdf';
    description = 'PDF Document';
  } else if (extension.toLowerCase() === 'csv') {
    mimeType = 'text/csv';
    description = 'CSV File';
  }
  
  await saveFileWithDialog(blob, fileName, description, extension, mimeType);
}

/**
 * Main export function that handles both PDF and CSV
 */
export async function exportTable(config: PrintConfig, tableConfig: TableConfig): Promise<void> {
  try {
    let blob: Blob;
    
    if (config.format.format === 'pdf') {
      blob = await exportToPDF(config, tableConfig); // Now async!
    } else {
      blob = exportToCSV(config, tableConfig);
    }
    
    const fileName = generateExportFileName(
      config.metadata.projectName,
      config.format.format,
      config.metadata.datePrinted
    );
    
    downloadBlob(blob, fileName);
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
}

// ===== Metadata Helpers =====

/**
 * Generate print metadata from project data
 */
export function generatePrintMetadata(
  projectName: string,
  projectData?: {
    customAttributes?: Record<string, any>;
    createdAt?: Date;
    modifiedAt?: Date;
  },
  author?: string
): PrintMetadata {
  return {
    projectName: projectName || 'Untitled Project',
    projectDescription: projectData?.customAttributes?.description || '',
    author: author || '',
    dateCreated: projectData?.createdAt || new Date(),
    dateModified: projectData?.modifiedAt || new Date(),
    datePrinted: new Date(),
    version: '1.0',
    companyName: projectData?.customAttributes?.company || '',
    customFields: {},
  };
}

/**
 * Validate print configuration
 */
export function validatePrintConfig(config: PrintConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Validate area
  if (!config.area || !config.area.type) {
    errors.push('Print area type is required');
  }
  
  // Validate format
  if (!config.format || !config.format.format) {
    errors.push('Output format is required');
  }
  
  // Validate PDF options
  if (config.format.format === 'pdf' && config.format.pdf) {
    const pdf = config.format.pdf;
    
    if (pdf.fontSize < 6 || pdf.fontSize > 24) {
      errors.push('Font size must be between 6 and 24');
    }
    
    if (pdf.margins.top < 0 || pdf.margins.right < 0 || pdf.margins.bottom < 0 || pdf.margins.left < 0) {
      errors.push('Margins cannot be negative');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}